/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import GlobalUtils from '../../../../utils/Globals';
import ThorUtils from '../../../../utils/ThorUtils';
import Button from '@react/react-spectrum/Button';
import wamClient from '@messaging/bifrost';
import NavHVAButtonActions from './NavHVAButtonActions';
import appHomeData from '../../../containers/AppHome/appHomeData';

interface Props {
	intl: any;
	history: Array<string>;
	sapCode: string;
	locale: string;
}

interface AppStatus {
	entitled: boolean;
	wam: boolean;
	installed: boolean;
	version: string;
	os: string;
}

const NavHVAButton = ({sapCode, locale, intl, history}: Props) => {
	const ACTIVE_SAPCODES = ['PHSP', 'ILST'];
	const [CCDPresent, setCCDPresent] = useState<boolean | null>(null);

	const thorProductsList = ThorUtils.getProductsList();

	useEffect(() => {
		wamClient.checkForCCD().then((isPresent) => {
			if (isPresent) {
				setCCDPresent(true);
				setStatus(checkAppStatus(sapCode));
			}
			else {
				setCCDPresent(false);
			}
		});
	}, []);

	const checkAppStatus = (sapCode) => {
		const status = {
			entitled: false,
			wam: false,
			installed: false,
			latest: false,
			version: '',
			os: 'other',
		};

		if (!GlobalUtils.isMobileOrTabletBrowser()) {
			status.os = 'desktop';
		}

		const CCDPresence: any = CCDPresent;
		CCDPresence === undefined || CCDPresence === null ? '' : (status.wam = CCDPresence);

		status.entitled = NavHVAButtonActions.checkEntitled(sapCode);

		if (thorProductsList) {
			status.wam = true;

			// App is installed to latest version
			status.installed = NavHVAButtonActions.containsApp(sapCode, thorProductsList.installed);
			if (status.installed) {
				status.version = NavHVAButtonActions.containsApp(sapCode, thorProductsList.installed).version;
				status.latest = true;
				return status;
			}

			// App is installed but not to latest version
			status.installed = NavHVAButtonActions.containsApp(sapCode, thorProductsList.available_updates);
			if (status.installed) {
				status.version = NavHVAButtonActions.containsApp(sapCode, thorProductsList.available_updates).version;
				status.latest = false;
				return status;
			}

			// App is not installed
		}

		return status;
	};

	const [status, setStatus] = useState<AppStatus | null>(checkAppStatus(sapCode));
	const isValidSapcode = ACTIVE_SAPCODES.includes(sapCode);

	const getCTAString = (sapcode, status) => {
		const appName = appHomeData[sapcode].data.appName;
		if (!status.entitled) {
			// CCD detected, user not entitled
			return intl.formatMessage({id: 'appHomes.download.try'});
		}
		else if (!status.wam) {
			// CCD not detected
			return intl.formatMessage({id: `appHomes.download.${appName}`});
		}
		else if (!status.installed) {
			// CCD detected, app not installed
			return intl.formatMessage({id: `appHomes.download.${appName}.install`});
		}
		// CCD detected, app installed
		return intl.formatMessage({id: 'appHomes.download.open'});

	};

	const renderNavCTA = (sapCode, status) => {
		if (!status.entitled) {
			// user is not entitled
			return (
				<Button
					variant='action'
					label={getCTAString(sapCode, status)}
					className='apphomes-download-btn'
					onClick={() => {
						window.location.href = window.CONFIG.PLANS_URL;
					}}
				/>
			);
		}
		else if ((status.os !== 'desktop' || !status.installed || !status.wam) && status.entitled) {
			// Not on desktop, CCD not detected, or CCD detected but app not installed
			return (
				<Button
					variant='action'
					label={getCTAString(sapCode, status)}
					className='apphomes-download-btn'
					onClick={() => {
						history.push(appHomeData[sapCode].data.installUrl);
					}}
				/>
			);
		}
		// CCD detected, app installed
		return (
			<Button
				variant='action'
				label={getCTAString(sapCode, status)}
				className='apphomes-download-btn'
				onClick={() => {
					NavHVAButtonActions.launchApp(sapCode, status, wamClient);
				}}
			/>
		);

	};

	const renderButton = (status) => (window.cchome.util.hasFeature('apphomes_cta') ? (
		renderNavCTA(sapCode, status)
	) : (
		<Link style={{textDecoration: 'none'}} to={appHomeData[sapCode].data.installUrl}>
			<Button variant='action' className='apphomes-download-btn'>
				{intl.formatMessage({id: `appHomes.download.${appHomeData[sapCode].data.appName}`})}
			</Button>
		</Link>
	));

	return <>{isValidSapcode && renderButton(status)}</>;
};

export default NavHVAButton;
